import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock, LogOut, RefreshCw, Phone, Mail, MapPin, Search, Inbox, Hammer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bbs_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-6" data-testid="admin-login-screen">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-brick flex items-center justify-center">
            <Hammer className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-extrabold text-white text-xl">BBS <span className="text-brick-light">Leads</span></span>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-brick" />
            <h1 className="font-heading font-bold text-charcoal text-lg">Owner Login</h1>
          </div>
          <label className="block text-charcoal/60 text-sm font-semibold mb-2">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            placeholder="Enter dashboard password"
            className="w-full rounded-lg border border-black/10 px-4 py-3 text-charcoal focus:outline-none focus:ring-2 focus:ring-brick"
          />
          {error && <p data-testid="admin-login-error" className="mt-3 text-brick text-sm font-medium">{error}</p>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-brick hover:bg-brick-dark text-white font-bold py-3 transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

const LeadsDashboard = ({ token, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        setError("Could not load leads. Try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter((l) => {
    const q = query.toLowerCase();
    return (
      l.name?.toLowerCase().includes(q) ||
      l.phone?.toLowerCase().includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.service?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-lightgray" data-testid="admin-dashboard">
      <header className="bg-charcoal text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-brick flex items-center justify-center">
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-lg">BBS <span className="text-brick-light">Leads</span></span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchLeads}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-brick hover:bg-brick-dark px-4 py-2 text-sm font-semibold transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-extrabold text-charcoal text-3xl sm:text-4xl">Estimate Requests</h1>
            <p className="text-charcoal/60 mt-1" data-testid="admin-lead-count">
              {leads.length} total lead{leads.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-charcoal/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, city…"
              className="w-full sm:w-72 rounded-full border border-black/10 bg-white pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brick"
            />
          </div>
        </div>

        {error && <p className="text-brick font-medium mb-4">{error}</p>}

        {loading ? (
          <p className="text-charcoal/50" data-testid="admin-loading">Loading leads…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-black/5" data-testid="admin-empty-state">
            <Inbox className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
            <p className="text-charcoal/60 font-medium">
              {leads.length === 0 ? "No leads yet. New estimate requests will appear here." : "No leads match your search."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-leads-list">
            {filtered.map((lead) => (
              <div
                key={lead.id}
                data-testid={`admin-lead-${lead.id}`}
                className="bg-white rounded-2xl p-6 border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-bold text-charcoal text-lg">{lead.name}</h3>
                    {lead.service && (
                      <span className="inline-block mt-1 text-xs font-bold text-brick bg-brick/10 rounded-full px-2.5 py-0.5">
                        {lead.service}
                      </span>
                    )}
                  </div>
                  <span className="text-charcoal/40 text-xs whitespace-nowrap">{formatDate(lead.created_at)}</span>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-charcoal font-semibold hover:text-brick transition-colors">
                    <Phone className="w-4 h-4 text-brick" /> {lead.phone}
                  </a>
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-charcoal/70 hover:text-brick transition-colors break-all">
                      <Mail className="w-4 h-4 text-brick" /> {lead.email}
                    </a>
                  )}
                  {lead.city && (
                    <p className="flex items-center gap-2 text-charcoal/70">
                      <MapPin className="w-4 h-4 text-brick" /> {lead.city}
                    </p>
                  )}
                </div>
                {lead.message && (
                  <p className="mt-4 text-charcoal/70 text-sm leading-relaxed bg-lightgray rounded-lg p-3">
                    “{lead.message}”
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <LeadsDashboard token={token} onLogout={logout} />;
}
